/**
 * Skeleton + DelayNotice (§4.4): meaningful skeletons only when the layout matches the final content; after ten
 * seconds show a plain-language delay message and a retry. Never a dead end (§1.2).
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from '../cn';
import { Button } from './Button';

export interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  /** Shape of the content this stands in for, so the layout matches (§4.4). */
  shape?: 'text' | 'block' | 'circle' | 'display';
  className?: string;
  style?: CSSProperties;
  /** Accessible label for the pending region (set on the wrapper, not on each bone). */
  label?: string;
}

export function Skeleton({ width, height, shape = 'block', className, style, label }: SkeletonProps) {
  const dims: CSSProperties = {
    width: width ?? (shape === 'circle' ? 48 : '100%'),
    height:
      height ?? (shape === 'text' ? '1em' : shape === 'display' ? '2.5em' : shape === 'circle' ? 48 : 120),
    borderRadius: shape === 'circle' ? '50%' : undefined,
    ...style,
  };
  return (
    <span
      className={cn('wd-skeleton', className)}
      style={dims}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'status' : undefined}
      aria-busy={label ? true : undefined}
    />
  );
}

export const DELAY_NOTICE_MS = 10_000;

export interface DelayNoticeProps {
  /** Milliseconds before the delay message appears (default 10s per §4.4). */
  after?: number;
  message?: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
  /** Alternate path shown beside retry (e.g. "Check status"). */
  secondary?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a pending region: renders `children` (the skeleton) and, once `after` elapses, the delay notice with a
 * retry action. Reset by remounting (e.g. keyed on the request id).
 */
export function DelayNotice({
  after = DELAY_NOTICE_MS,
  message = 'This is taking longer than usual. Your information is safe — you can retry or check status.',
  retryLabel = 'Retry',
  onRetry,
  secondary,
  children,
  className,
}: DelayNoticeProps) {
  const [late, setLate] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setLate(true), after);
    return () => window.clearTimeout(t);
  }, [after]);
  return (
    <div className={className} aria-busy={!late}>
      {children}
      {late ? (
        <div className="wd-delay" role="status">
          <p style={{ margin: 0 }}>{message}</p>
          <div className="wd-btn-group">
            {onRetry ? (
              <Button variant="secondary" onClick={onRetry}>
                {retryLabel}
              </Button>
            ) : null}
            {secondary}
          </div>
        </div>
      ) : null}
    </div>
  );
}
